import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { formatShopifyFieldValue, type FormattedValue } from "@/features/inventory/item-detail/components/utils/shopifyFormatting";
import { cn } from "@/lib/utils";

interface ChildTablePreviewProps {
  field: {
    fieldname: string;
    label?: string;
    options?: string; // child doctype name
  };
  value: any; // child table array
  currency?: string;
  maxRows?: number; // max rows to show before "show more"
  maxFields?: number; // max fields to show per row
}

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Read-only mini table preview for child tables
 * Shows a compact table view of child table data before opening the full modal
 */
export function ChildTablePreview({
  field,
  value,
  currency,
  maxRows = 3,
  maxFields = 3,
}: ChildTablePreviewProps) {
  const childDoctype = field.options || "";

  const { data: childSchema, isLoading } = useQuery({
    queryKey: ["meta", childDoctype],
    queryFn: () => erp.meta.getDocTypeSchema(childDoctype),
    enabled: !!childDoctype,
  });

  // Normalize value to array
  const rows = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) {
      // Filter out soft-deleted rows
      return value.filter((row) => !row.__deleted);
    }
    if (typeof value === "object") {
      const rows = value.rows || value.data;
      if (Array.isArray(rows)) {
        return rows.filter((row) => !row.__deleted);
      }
    }
    return [];
  }, [value]);

  // Determine visible fields (prefer in_list_view, fallback to first few key fields)
  const visibleFields = useMemo(() => {
    if (!childSchema) return [];

    // Get all non-hidden, non-system fields
    const allNonSystemFields = childSchema.fields.filter(
      (f) =>
        f.hidden !== 1 &&
        !f.fieldname.startsWith("__") &&
        !["idx", "docstatus", "__islocal", "name", "parent", "parenttype", "parentfield"].includes(
          f.fieldname
        )
    );

    // Prefer in_list_view fields
    const listViewFields = allNonSystemFields.filter((f) => {
      const v = (f as any).in_list_view;
      return v === 1 || v === true || v === "1";
    });

    if (listViewFields.length > 0) {
      return listViewFields.slice(0, maxFields);
    }

    // Fallback: avoid long text/editor fields, take first few
    const gridCandidates = allNonSystemFields.filter(
      (f) => !["Text Editor", "Long Text", "Small Text", "HTML"].includes(f.fieldtype)
    );

    return (gridCandidates.length > 0 ? gridCandidates : allNonSystemFields).slice(0, maxFields);
  }, [childSchema, maxFields]);

  const totalCount = rows.length;
  const displayRows = rows.slice(0, maxRows);
  const hasMore = totalCount > maxRows;

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
        No items
      </span>
    );
  }

  if (visibleFields.length === 0) {
    // Fallback to badge if no fields available
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
        {totalCount} item{totalCount === 1 ? "" : "s"}
      </span>
    );
  }

  return (
    <div className="space-y-3">
      {/* Mini table - Apple Cart Style */}
      <div className="bg-white dark:bg-gray-900 rounded-md shadow-sm border border-gray-200/50 dark:border-gray-800/50 overflow-hidden backdrop-blur-sm">
        <div className="divide-y divide-gray-100/50 dark:divide-gray-800/30">
          {displayRows.map((row, rowIndex) => (
            <div
              key={row.name || row._localId || `row-${rowIndex}`}
              className="px-5 py-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200"
            >
              <div 
                className="grid gap-6 items-start"
                style={{ 
                  gridTemplateColumns: visibleFields.length === 1 
                    ? '1fr' 
                    : visibleFields.length === 2
                    ? '2fr 1fr'
                    : visibleFields.length === 3
                    ? '2fr 1fr 1fr'
                    : `2fr repeat(${visibleFields.length - 1}, 1fr)`
                }}
              >
                {visibleFields.map((fieldDef, fieldIndex) => {
                  const fieldValue = row[fieldDef.fieldname];
                  const formatted = formatShopifyFieldValue(fieldDef, fieldValue, currency);
                  
                  return (
                    <div 
                      key={fieldDef.fieldname} 
                      className="min-w-0"
                    >
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide uppercase">
                          {fieldDef.label}
                        </div>
                        <div className={cn(
                          "font-medium text-gray-900 dark:text-gray-100 truncate",
                          fieldIndex === 0 ? "text-base tracking-tight" : "text-sm"
                        )}>
                          {renderFormattedValue(formatted)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer with count and "show more" indicator */}
      <div className="flex items-center justify-between px-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {totalCount} item{totalCount === 1 ? "" : "s"}
        </span>
        {hasMore && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            +{totalCount - maxRows} more
          </span>
        )}
      </div>
    </div>
  );
}

